import Button from 'components/Globals/Base/Button';
import useClickOutside from 'hooks/useClickOutside';
import useLockBodyScroll from 'hooks/useLockBodyScroll';
import { type JSX, useEffect, useRef } from 'react';
import type { ButtonVariant } from 'components/Globals/Base/Button/types';
import * as S from './ModalStyles';

export interface ModalButtonProps {
  onClick: () => void;
  title: string;
  variant?: ButtonVariant;
  dataCy?: string;
}

interface ModalProps {
  className?: string;
  isOpened: boolean;
  title: string;
  children: string | JSX.Element;
  onClose: (e?: React.MouseEvent<HTMLButtonElement> | KeyboardEvent) => void;
  firstButton?: ModalButtonProps;
  secondButton?: ModalButtonProps;
}

const Modal = ({ className, isOpened = false, title, children, firstButton, secondButton, onClose }: ModalProps) => {
  const dialogRef = useRef<HTMLDivElement>(null);

  useClickOutside(dialogRef, onClose);
  useLockBodyScroll(isOpened);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isOpened && e.key === 'Escape') onClose(e);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpened, onClose]);

  return isOpened ? (
    <S.ModalWrapper
      className={className}
      role='dialog'
      aria-modal='true'
      aria-labelledby='modalTitle'
      aria-describedby='modalContent'
    >
      <S.Dialog
        ref={dialogRef}
        className='modal-wrapper'
      >
        <S.CloseButton
          onClick={(e) => onClose(e)}
          aria-label='Close'
        />
        <S.ModalTitle
          id='modalTitle'
          as='h2'
          $fontFamily='graphikCond'
          $size={{ default: 'level3', mdMin: 'level2' }}
          data-cy='modal-title'
        >
          {title}
        </S.ModalTitle>
        <div id='modalContent'>{children}</div>
        <S.ModalButtons>
          {firstButton && (
            <Button
              type='button'
              onClick={firstButton.onClick}
              variant={firstButton.variant || 'primary'}
              hasRightArrow={false}
              dataCy={firstButton?.dataCy}
            >
              {firstButton.title}
            </Button>
          )}
          {secondButton && (
            <Button
              className='second-btn'
              type='button'
              onClick={secondButton.onClick}
              variant={secondButton.variant || 'secondary'}
              hasRightArrow={false}
              dataCy={secondButton?.dataCy}
            >
              {secondButton.title}
            </Button>
          )}
        </S.ModalButtons>
      </S.Dialog>
    </S.ModalWrapper>
  ) : null;
};

export default Modal;
